import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { AdminPanelSettings, Upload } from '@mui/icons-material';
import { carImportApi } from '../api/apiClient';

interface AdminPanelProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ open, onClose, onUploadSuccess }) => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Simple password check - in production, use proper authentication
  const ADMIN_PASSWORD = 'carimport2025';

  const handlePasswordSubmit = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      await carImportApi.uploadFile(selectedFile);
      setSuccess(`Successfully imported data from ${selectedFile.name}`);
      setSelectedFile(null);
      onUploadSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setIsAuthenticated(false);
    setSelectedFile(null);
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
        color: 'white'
      }}>
        <AdminPanelSettings />
        Admin Panel
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {!isAuthenticated ? (
          <Box>
            <Typography variant="body1" gutterBottom>
              Enter admin password to access import functionality:
            </Typography>
            <TextField
              fullWidth
              type="password"
              label="Admin Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              error={!!error}
              helperText={error}
              sx={{ mt: 2 }}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Excel File Import
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload an Excel file (.xls or .xlsx) containing car import tax data.
            </Typography>

            <Box sx={{ 
              border: '2px dashed',
              borderColor: selectedFile ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              backgroundColor: selectedFile ? 'primary.50' : 'grey.50',
              mb: 2
            }}>
              <input
                accept=".xls,.xlsx"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload />}
                  sx={{ mb: 1 }}
                >
                  Select Excel File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
              sx={{ mt: 2 }}
            >
              {uploading ? 'Importing...' : 'Import Data'}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {!isAuthenticated ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handlePasswordSubmit} variant="contained">
              Login
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdminPanel;
